import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { articlesByCategory } from '@/mocks/articles';
import type { ArticleType } from '@/mocks/articles';

const articleThumbnails: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=modern%20gaming%20platform%20announcement%20with%20warm%20golden%20tones%2C%20abstract%20technology%20concept%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-01&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=secure%20login%20security%20concept%20with%20warm%20golden%20shield%20icon%2C%20abstract%20technology%20protection%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-02&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=gaming%20strategy%20guide%20concept%20with%20warm%20golden%20accents%2C%20abstract%20chess%20pieces%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-03&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=cybersecurity%20protection%20shield%20concept%20with%20warm%20amber%20glow%2C%20abstract%20firewall%20visualization%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-04&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=futuristic%20platform%20update%20concept%20with%20golden%20geometric%20shapes%2C%20abstract%20digital%20transformation%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-05&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=premium%20VIP%20membership%20concept%20with%20golden%20crown%2C%20abstract%20luxury%20design%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20professional%20atmosphere%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-06&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=player%20growth%20journey%20concept%20with%20warm%20golden%20path%2C%20abstract%20progression%20steps%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-07&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=account%20recovery%20security%20concept%20with%20golden%20lock%2C%20abstract%20protection%20design%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-08&orientation=landscape',
  'article-9': 'https://readdy.ai/api/search-image?query=data%20analysis%20strategy%20concept%20with%20warm%20golden%20charts%2C%20abstract%20analytics%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-09&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=mobile%20gaming%20platform%20concept%20with%20golden%20device%2C%20abstract%20smartphone%20display%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=gaming%20event%20celebration%20concept%20with%20warm%20golden%20confetti%2C%20abstract%20festive%20atmosphere%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-11&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=gaming%20glossary%20dictionary%20concept%20with%20warm%20golden%20book%2C%20abstract%20knowledge%2C%20clean%20minimalist%20style%2C%20soft%20lighting%2C%20no%20text%20no%20people&width=800&height=450&seq=cat-thumb-12&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [filteredArticles, setFilteredArticles] = useState<ArticleType[]>([]);
  const [activeTab, setActiveTab] = useState(slug || '');
  const currentCategory = categoryMap[slug || ''] || categories[0];

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
      setFilteredArticles(articlesByCategory[slug] || []);
    }
  }, [slug]);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}`}
        description={`众赢zygjb${currentCategory.name} - ${currentCategory.description}`}
        keywords={`众赢zygjb,${currentCategory.name},zygjb平台,众赢官方入口`}
        canonicalUrl={`https://zylogin.github.io/category/${currentCategory.slug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-4 bg-gradient-to-b from-background-100 to-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-2xl border border-background-200/70 bg-white hover:border-primary-200 transition-all duration-300 overflow-hidden"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={articleThumbnails[article.id]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                          {article.tags[0]}
                        </span>
                        <span className="text-xs text-foreground-500">{article.publishDate}</span>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.readCount.toLocaleString()} 阅读
                        </span>
                        <span className="flex items-center gap-1 text-primary-500 font-medium">
                          阅读全文
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
                <Link
                  to="/category/platform-news"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-500 font-medium"
                >
                  浏览其他分类
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 众赢zygjb官方入口`,
            description: currentCategory.description,
            url: `https://zylogin.github.io/category/${currentCategory.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '众赢zygjb官方入口',
              url: 'https://zylogin.github.io',
            },
          }),
        }}
      />
    </>
  );
}